// A one-time copy of a folder (Firefox and Safari, which cannot keep a live folder open, or
// "Import a copy"): picked with <input webkitdirectory> or dropped onto the page. It keeps every
// file (not build output or other checkouts) and the files of its `.git` folder, for the Files tab.
import { IGNORED_DIRECTORIES, isCheckout, withoutNestedCheckouts, type SourceFileEntry } from '@modelviewer/core';
import { isRelevantFile, type WorkspaceSource } from './types';

/** A file of the copy: read already, or a dropped entry read when needed. */
type CopiedFile = File | FileSystemFileEntry;

/** The copy's git repository: the files of its `.git` folder by path inside it, or its `.git` file. */
export interface GitCopy {
  folder: Map<string, File>;
  /** `.git` as a file: a linked worktree or a submodule, whose history is elsewhere. */
  pointer: File | null;
}

const fileOf = (entry: FileSystemFileEntry) => new Promise<File>((resolve, reject) => entry.file(resolve, reject));

export class UploadSource implements WorkspaceSource {
  readonly kind = 'upload' as const;
  readonly live = false;

  constructor(
    readonly id: string,
    readonly label: string,
    private readonly files: Map<string, CopiedFile>,
    readonly git: GitCopy,
  ) {}

  async list(): Promise<SourceFileEntry[]> {
    return [...this.files.keys()]
      .filter(isRelevantFile)
      .sort()
      .map((path) => ({ path }));
  }

  /** Every file of the copy, for the Files tab. */
  paths(): string[] {
    return [...this.files.keys()].sort();
  }

  async file(path: string): Promise<File | null> {
    const file = this.files.get(path);
    if (!file) return null;
    return file instanceof File ? file : fileOf(file);
  }

  async read(path: string): Promise<string> {
    const file = await this.file(path);
    if (!file) throw new Error(`File not found: ${path}`);
    return file.text();
  }

  /** From an `<input type="file" webkitdirectory>` selection. */
  static fromFileList(list: FileList): UploadSource {
    const all = new Map<string, File>();
    let root = '';
    for (const file of Array.from(list)) {
      const relative = file.webkitRelativePath || file.name;
      const slash = relative.indexOf('/');
      root = root || (slash > 0 ? relative.slice(0, slash) : '');
      all.set(slash > 0 ? relative.slice(slash + 1) : relative, file);
    }
    const files = new Map<string, CopiedFile>();
    const git: GitCopy = { folder: new Map(), pointer: null };
    for (const path of withoutNestedCheckouts([...all.keys()])) {
      if (path === '.git') git.pointer = all.get(path)!;
      else if (path.startsWith('.git/')) git.folder.set(path.slice(5), all.get(path)!);
      else if (!path.split('/').some((s) => IGNORED_DIRECTORIES.has(s))) files.set(path, all.get(path)!);
    }
    return new UploadSource(`upload:${root || 'folder'}`, root || 'Uploaded folder', files, git);
  }

  /** From a folder dropped onto the page (browsers without file system handles). */
  static async fromEntry(entry: FileSystemDirectoryEntry): Promise<UploadSource> {
    const files = new Map<string, CopiedFile>();
    const git: GitCopy = { folder: new Map(), pointer: null };
    const readAll = (dir: FileSystemDirectoryEntry) =>
      new Promise<FileSystemEntry[]>((resolve, reject) => {
        const reader = dir.createReader();
        const all: FileSystemEntry[] = [];
        const next = () =>
          reader.readEntries((batch) => {
            if (batch.length === 0) resolve(all);
            else {
              all.push(...batch);
              next();
            }
          }, reject);
        next();
      });
    // The repository's own .git, read whole (it is small next to the code, and the worker needs its files).
    const readGit = async (dir: FileSystemDirectoryEntry, prefix: string): Promise<void> => {
      for (const e of await readAll(dir)) {
        if (e.isDirectory) await readGit(e as FileSystemDirectoryEntry, `${prefix}${e.name}/`);
        else git.folder.set(`${prefix}${e.name}`, await fileOf(e as FileSystemFileEntry));
      }
    };
    const walk = async (dir: FileSystemDirectoryEntry, prefix: string, insideCheckout: boolean): Promise<void> => {
      const entries = await readAll(dir);
      const checkout = isCheckout(entries.map((e) => e.name));
      if (checkout && insideCheckout) return;
      for (const e of entries) {
        if (prefix === '' && e.name === '.git') {
          if (e.isDirectory) await readGit(e as FileSystemDirectoryEntry, '');
          else git.pointer = await fileOf(e as FileSystemFileEntry);
        } else if (e.isDirectory) {
          if (!IGNORED_DIRECTORIES.has(e.name)) await walk(e as FileSystemDirectoryEntry, `${prefix}${e.name}/`, insideCheckout || checkout);
        } else {
          files.set(`${prefix}${e.name}`, e as FileSystemFileEntry);
        }
      }
    };
    await walk(entry, '', false);
    return new UploadSource(`upload:${entry.name}`, entry.name, files, git);
  }
}
